import { useEffect, useRef } from 'react';

// Coordinates Escape-to-close across all overlays (Modal, Popover, ContextMenu,
// Lightbox, etc.) so a single Escape press closes only the topmost one.
//
// Without coordination, every overlay registers its own document keydown
// listener; on Escape they all fire and every overlay closes at once,
// regardless of stacking order.
//
// Also tracks an outside-click stack so a popover whose descendant popover
// is open won't dismiss when the user interacts with that descendant. The
// descendant's content is portal-rendered, so DOM containment alone makes
// it look "outside" the parent. The stack lets each popover treat clicks
// inside any popover registered after it as "still inside".

/**
 * Returning `false` DECLINES the Escape press: the stack leaves the event
 * alone so element-level handlers (command palette, inline edits, recorders)
 * still see it. Everything else claims the press as before.
 *
 * Only long-lived, surface-level dismissers need this — a dismisser that
 * lives exactly as long as its overlay is always the correct target while
 * it's on top. The settings mode is the motivating case: it registers for
 * the whole mode, so it must yield to anything focused inside it.
 */
type Dismisser = () => void | boolean;

const stack: Dismisser[] = [];
let listenerAttached = false;

// ── In-editor Escape claims ────────────────────────────────────────────────
// ProseMirror preventDefaults EVERY Escape (prosemirror-view capturekeys), so
// `defaultPrevented` cannot tell "a popup consumed this press" from "a bare
// press in the editor". In-editor Escape consumers (mention/slash/emoji
// suggestion popups, the paste-format popover) claim the native event here;
// the keyboard dispatcher's composer-escape path yields to claimed presses
// instead of escaping the panel over a popup that just closed.
const claimedEscapePresses = new WeakSet<Event>();

export const claimEscapePress = (event: Event) => {
	claimedEscapePresses.add(event);
};

export const isEscapePressClaimed = (event: Event) => claimedEscapePresses.has(event);

interface ClickDismissEntry {
	id: number;
	/** Returns true if `target` falls inside this popover's own refs. */
	isInside: (target: Node) => boolean;
}

let nextClickDismissId = 1;
const clickDismissStack: ClickDismissEntry[] = [];

/**
 * Register a popover's "is the click inside me?" check on the global stack.
 *
 * Callers receive an `isInsideSelfOrLater(target)` helper which returns
 * true when `target` is inside this popover OR inside any popover pushed
 * onto the stack after this one (i.e. a descendant popover). This is the
 * predicate every popover's outside-click handler should consult before
 * closing — without it, opening a `<Dropdown>` inside a `<Popover>` causes
 * the outer popover to dismiss as soon as the user clicks an option in
 * the inner one (the inner's content is portal-rendered, so DOM
 * containment on the outer alone reports it as "outside").
 */
export const pushClickDismisser = (isInside: (target: Node) => boolean) => {
	const id = nextClickDismissId++;
	clickDismissStack.push({ id, isInside });

	const isInsideSelfOrLater = (target: Node) => {
		const idx = clickDismissStack.findIndex((e) => e.id === id);
		if (idx === -1) return false;
		for (let i = idx; i < clickDismissStack.length; i++) {
			if (clickDismissStack[i].isInside(target)) return true;
		}
		return false;
	};

	const unregister = () => {
		const i = clickDismissStack.findIndex((e) => e.id === id);
		if (i !== -1) clickDismissStack.splice(i, 1);
	};

	return { isInsideSelfOrLater, unregister };
};

const handleKeyDown = (e: KeyboardEvent) => {
	if (e.key !== 'Escape') return;
	const top = stack[stack.length - 1];
	if (!top) return;
	// Ask before claiming. This listener is capture-phase and stops immediate
	// propagation, so claiming an Escape hides it from React's delegated
	// handlers entirely — a dismisser that declines must not do that.
	if (top() === false) return;
	e.stopPropagation();
	e.stopImmediatePropagation();
};

const ensureListener = () => {
	if (listenerAttached || typeof document === 'undefined') return;
	document.addEventListener('keydown', handleKeyDown, true);
	listenerAttached = true;
};

export const pushEscapeDismisser = (fn: Dismisser) => {
	ensureListener();
	stack.push(fn);
	return () => {
		const i = stack.lastIndexOf(fn);
		if (i !== -1) stack.splice(i, 1);
	};
};

export const hasEscapeDismisser = () => stack.length > 0;

export const useEscapeDismiss = (
	open: boolean,
	onClose: () => void | boolean,
) => {
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	useEffect(() => {
		if (!open) return;
		return pushEscapeDismisser(() => onCloseRef.current());
	}, [open]);
};
